import { createApp as createVueApp, h, createSSRApp } from 'vue';
import { SitecoreJssPlaceholderPlugin } from '@sitecore-jss/sitecore-jss-vue';
import { createHead } from '@unhead/vue/client';
import { createHead as createSSRHead } from '@unhead/vue/server';
import AppRoot from './AppRoot';
import { createRouter } from './router';
import componentFactory from './temp/componentFactory';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { createPinia } from 'pinia';
import { useJssStore } from './stores/jssStore';

// createApp is invoked by both the main and SSR entry points, so the two entry points can use the same app creation process.
export function createApp(initialState, i18n, isSSR) {
  const router = createRouter(isSSR);
  const head = isSSR
    ? createSSRHead({
        disableDefaults: true,
      })
    : createHead();
  const pinia = createPinia();

  const vueOptions = {
    router,
    render: () => h(AppRoot),
    i18n,
  };

  const app = isSSR ? createSSRApp(vueOptions) : createVueApp(vueOptions);

  app.use(router);
  app.use(pinia);
  app.use(SitecoreJssPlaceholderPlugin, { componentFactory });
  app.use(i18n);
  app.use(head);
  app.use(VueQueryPlugin);

  if (!isSSR) {
    // Client: hydrate pinia state
    pinia.state.value = initialState?.pinia || {};
  }

  const jssStore = useJssStore(pinia);

  if (isSSR) {
    // SSR: initialize jssStore with state data and save it for serialization
    if (initialState?.sitecore?.context) {
      jssStore.patchState(initialState.sitecore.context, initialState.sitecore?.route);
    }
    initialState.pinia = pinia.state.value;
  }

  Object.defineProperty(app.config.globalProperties, '$isPageEditing', {
    get() {
      return jssStore.pageEditing;
    },
    enumerable: true,
    configurable: true,
  });

  return { app, router, initialState, head };
}
